import React, { useState } from 'react';
import logoGeorge from '../assets/logo_george.png';

const DemoHeader = () => (
  <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
    <div className="flex items-center gap-4">
      {/* Hamburger Icon */}
      <button className="text-black hover:text-gray-600 transition-colors">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>
    </div>

    {/* Centered Logo */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <img src={logoGeorge} alt="Tiffany & Co." className="h-8 object-contain opacity-90" />
    </div>

    {/* Right Action */}
    <div className="text-[10px] font-bold tracking-widest uppercase text-gray-900 cursor-pointer hover:text-[#81D8D0] transition-colors">
      New Chat
    </div>
  </div>
);

const DemoWindow = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full h-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-200 overflow-hidden flex flex-col">
    <DemoHeader />
    <div className="flex-1 relative overflow-hidden bg-[#f9f9f9]">{children}</div>
  </div>
);

const DemoInput = ({ placeholder = 'Write to George Retail Assistant' }) => (
  <div className="mt-auto pt-4">
    <div className="relative group">
      <input
        type="text"
        placeholder={placeholder}
        className="w-full pl-4 pr-10 py-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#00C0BE] focus:ring-1 focus:ring-[#00C0BE] shadow-sm text-gray-600 transition-all placeholder-gray-400 text-xs"
        disabled
      />
      <div className="absolute right-2 top-1/2 -translate-x-1/2 bg-gray-300 rounded-md p-1.5 text-white group-hover:bg-[#00C0BE] transition-colors cursor-pointer">
        <svg
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          viewBox="0 0 24 24"
        >
          <line x1="12" y1="19" x2="12" y2="5"></line>
          <polyline points="5 12 12 5 19 12"></polyline>
        </svg>
      </div>
    </div>
  </div>
);

// --- DEMO CONTENT 1: PRICING ---
const PricingContent = () => (
  <div className="p-5 h-full flex flex-col font-sans relative text-xs">
    {/* Msg 1 */}
    <div className="flex mb-6 animate-fade-in-up">
      <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 max-w-[85%] text-gray-800 leading-relaxed">
        <p>Hello 👋, I am George Servicing Assistant! How can I help?</p>
      </div>
    </div>

    {/* Msg 2 */}
    <div
      className="flex mb-6 flex-row-reverse animate-fade-in-up"
      style={{ animationDelay: '100ms' }}
    >
      <div className="bg-[#00C0BE] px-4 py-3 rounded-2xl rounded-tr-none shadow-sm max-w-[85%] text-white leading-relaxed">
        <p>Hello ! Give me the price to lengthen a light pendant chain in gold</p>
      </div>
    </div>

    {/* Msg 3 */}
    <div className="flex mb-6 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
      <div className="flex-1 max-w-[95%]">
        <div className="bg-white rounded-2xl rounded-tl-none shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4">
            <p className="text-gray-800 mb-4 leading-relaxed">
              Hello! Here&apos;s the pricing for lengthening a light pendant chain in gold:
            </p>

            <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 text-[10px] sm:text-xs">
              <div className="grid grid-cols-12 bg-[#00C0BE] text-white font-semibold py-2 px-3">
                <div className="col-span-5">Service</div>
                <div className="col-span-3">Price</div>
                <div className="col-span-4">Lead Time</div>
              </div>
              <div className="grid grid-cols-12 py-3 px-3 bg-gray-50/50 items-center border-t border-gray-100">
                <div className="col-span-5 font-medium text-gray-900 pr-2">
                  Light Pendant Chain Lengthening (Gold/Platinum)
                </div>
                <div className="col-span-3 text-gray-700">€200 (Flat Rate)</div>
                <div className="col-span-4 text-gray-700">3 weeks (COM) / 10 days (NSA)</div>
              </div>
            </div>

            <div className="mb-3 text-gray-600 leading-relaxed">
              <span className="font-bold text-gray-900">Details:</span> This is a flat rate price
              for light pendant chains like Peretti, Knot, and Heart pendants. The service includes
              professional polishing.
            </div>

            <div className="text-gray-600">
              <span className="font-bold text-gray-900">Source:</span>{' '}
              <span className="text-[#00C0BE] cursor-pointer hover:underline font-medium">
                Service-Pricing-Al-EURO
              </span>
            </div>
          </div>
          {/* Footer Actions */}
          <div className="bg-gray-50 px-4 py-2 flex justify-end gap-3 text-gray-400 border-t border-gray-100">
            <svg
              className="w-4 h-4 cursor-pointer hover:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <svg
              className="w-4 h-4 cursor-pointer hover:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <DemoInput />
  </div>
);

// --- DEMO CONTENT 2: POLICY ---
const PolicyContent = () => (
  <div className="p-5 h-full flex flex-col font-sans relative text-xs overflow-hidden">
    {/* Msg 1 */}
    <div className="flex mb-6 animate-fade-in-up">
      <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 max-w-[85%] text-gray-800 leading-relaxed">
        <p>Hello 👋, I am George Retail Assistant! How can I help?</p>
      </div>
    </div>

    {/* Msg 2: User Hold Question */}
    <div
      className="flex mb-6 flex-row-reverse animate-fade-in-up"
      style={{ animationDelay: '100ms' }}
    >
      <div className="bg-[#00C0BE] px-4 py-3 rounded-2xl rounded-tr-none shadow-sm max-w-[85%] text-white leading-relaxed">
        <p>Hello ! How long can I put an item on hold for ?</p>
      </div>
    </div>

    {/* Msg 3: Bot Hold Answer */}
    <div className="flex mb-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
      <div className="flex-1 max-w-[100%]">
        <div className="bg-white rounded-2xl rounded-tl-none shadow-sm border border-gray-100 p-4 leading-relaxed text-gray-700">
          <p className="mb-2">
            The hold duration depends on the{' '}
            <span className="font-bold text-gray-900">product type and deposit collected</span>:
          </p>
          <ul className="space-y-2 mb-3">
            <li>
              • <span className="font-bold text-gray-900">Standard items</span>: 3 days with{' '}
              <span className="font-bold text-gray-900">no deposit</span> (no exceptions allowed)
            </li>
            <li>
              • <span className="font-bold text-gray-900">Wedding Bands</span>: 7 days with{' '}
              <span className="font-bold text-gray-900">50% deposit</span>
            </li>
            <li>
              • <span className="font-bold text-gray-900">High Jewelry</span>: 30 days with{' '}
              <span className="font-bold text-gray-900">30% deposit</span>
            </li>
          </ul>
          <div className="text-gray-500 text-[10px]">
            <span className="font-bold text-gray-900">Source:</span>{' '}
            <span className="text-[#00C0BE] cursor-pointer hover:underline">Client Requests</span>
          </div>
        </div>
      </div>
    </div>

    {/* Msg 4: User Deposit Question */}
    <div
      className="flex mb-6 flex-row-reverse animate-fade-in-up"
      style={{ animationDelay: '300ms' }}
    >
      <div className="bg-[#00C0BE] px-4 py-3 rounded-2xl rounded-tr-none shadow-sm max-w-[85%] text-white leading-relaxed">
        <p>What if I have a deposit?</p>
      </div>
    </div>

    {/* Msg 5: Bot Deposit Answer */}
    <div className="flex mb-2 animate-fade-in-up" style={{ animationDelay: '400ms' }}>
      <div className="flex-1 max-w-[100%]">
        <div className="bg-white rounded-2xl rounded-tl-none shadow-sm border border-gray-100 p-4 leading-relaxed text-gray-700">
          <p className="mb-2">With a deposit, hold durations extend significantly:</p>
          <ul className="space-y-2 mb-3">
            <li>
              • <span className="font-bold text-gray-900">Wedding Bands: 7 days</span> with{' '}
              <span className="font-bold text-gray-900">50% deposit</span>
            </li>
            <li>
              • <span className="font-bold text-gray-900">Standard items</span>: Still{' '}
              <span className="font-bold text-gray-900">3 days maximum</span> regardless of deposit
            </li>
          </ul>
        </div>
      </div>
    </div>

    <DemoInput />
  </div>
);

// --- DEMO CONTENT 3: ANALYTICS (NEW) ---
const AnalyticsContent = () => {
  const chartData = [
    { label: 'USA', value: 355 },
    { label: 'Japan', value: 123 },
    { label: 'China', value: 74 },
    { label: 'S.Korea', value: 32 },
    { label: 'UK', value: 23 },
    { label: 'Canada', value: 23 },
    { label: 'HK', value: 21 },
    { label: 'Aus', value: 20 },
    { label: 'Mexico', value: 16 },
    { label: 'Germany', value: 15 },
  ];

  return (
    <div className="p-5 h-full flex flex-col font-sans relative text-xs">
      {/* Msg 1 */}
      <div className="flex mb-4 animate-fade-in-up">
        <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 max-w-[85%] text-gray-800 leading-relaxed">
          <p>Hello 👋, I am George Analytics Assistant! How can I help?</p>
        </div>
      </div>

      {/* Msg 2: Complex Query */}
      <div
        className="flex mb-4 flex-row-reverse animate-fade-in-up"
        style={{ animationDelay: '100ms' }}
      >
        <div className="bg-[#00C0BE] px-4 py-3 rounded-2xl rounded-tr-none shadow-sm max-w-[90%] text-white leading-relaxed">
          <p>
            Hello George, give me the repartition of channels by country (no other country), make it
            a bar chart, display only the top 10 and sort it
          </p>
        </div>
      </div>

      {/* Msg 3: Chart Response */}
      <div className="flex mb-2 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
        <div className="flex-1 max-w-[100%]">
          {/* Chart Container */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden p-2 md:p-4">
            <h4 className="font-bold text-center text-gray-900 mb-6">
              Count of Channels by Country
            </h4>

            {/* Bar Chart Visualization */}
            <div className="relative w-full mb-6 bg-white rounded-lg p-2 md:p-4">
              {/* Chart Container */}
              <div className="flex items-end justify-center gap-1 md:gap-3 h-56">
                {chartData.map((item) => {
                  const maxValue = 355; // USA value for consistent proportions
                  const heightPercentage = (item.value / maxValue) * 100;

                  return (
                    <div
                      key={item.label}
                      className="flex flex-col items-start group/bar w-[18px] md:w-[30px]"
                    >
                      {/* Bar Container */}
                      <div className="relative h-[200px] w-[18px] md:w-[30px]">
                        {/* Value Label Above Bar */}
                        <div
                          className="absolute left-1/2 -translate-x-1/2 text-[9px] md:text-[10px] font-semibold text-gray-700 transition-all duration-1000 ease-out whitespace-nowrap"
                          style={{
                            bottom: `${heightPercentage}%`,
                            marginBottom: '4px',
                          }}
                        >
                          {item.value}
                        </div>

                        {/* Background Track */}
                        <div
                          className="absolute bottom-0 left-0 right-0 flex items-end"
                          style={{ height: '100%' }}
                        >
                          {/* Filled Bar with Gradient */}
                          <div
                            className="rounded-t-lg transition-all duration-1000 ease-out relative overflow-hidden shadow-lg w-[18px] md:w-[30px]"
                            style={{
                              height: `${heightPercentage}%`,
                              background:
                                'linear-gradient(180deg, #5eead4 0%, #2dd4bf 25%, #14b8a6 50%, #0d9488 75%, #0f766e 100%)',
                              minHeight: '12px',
                            }}
                          >
                            {/* Shine Effect */}
                            <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent"></div>

                            {/* Top Highlight */}
                            <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-white/30 to-transparent rounded-t-lg"></div>

                            {/* Side Highlights for 3D effect */}
                            <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-r from-white/20 to-transparent"></div>
                            <div className="absolute top-0 bottom-0 right-0 w-1 bg-gradient-to-l from-black/10 to-transparent"></div>
                          </div>
                        </div>
                      </div>

                      {/* Country Label Below */}
                      <div className="text-[9px] font-medium text-gray-600 mt-6 ml-1 transform -rotate-45 origin-top-left whitespace-nowrap">
                        {item.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Explanation Text */}
            <div className="mt-8 text-[10px] leading-relaxed text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
              The bar chart displays the top 10 countries by the number of channels. The USA has the
              highest number of channels with 355, followed by Japan with 123, and China with 74.
            </div>

            {/* Footer Icons */}
            <div className="flex justify-end gap-3 mt-3 text-gray-400">
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <DemoInput />
    </div>
  );
};

const PlatformDemo: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const slides = [
    { id: 'pricing', content: <PricingContent />, label: 'Servicing Agent' },
    { id: 'policy', content: <PolicyContent />, label: 'Retail Agent' },
    { id: 'analytics', content: <AnalyticsContent />, label: 'Analytics Agent' },
  ];

  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 mb-12 md:mb-16 relative">
      {/* Carousel Controls Container */}
      <div className="relative group h-[600px] flex items-center justify-center overflow-hidden">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#81D8D0]/20 via-blue-100/30 to-purple-100/20 blur-[80px] rounded-full pointer-events-none z-0"></div>

        {/* Carousel Items */}
        {slides.map((slide, index) => {
          let position = 'hidden';
          if (index === activeIndex) {
            position = 'center';
          } else if (index === (activeIndex + 1) % slides.length) {
            position = 'right';
          } else if (index === (activeIndex - 1 + slides.length) % slides.length) {
            position = 'left';
          }

          let styleClass = '';
          switch (position) {
            case 'center':
              styleClass = 'left-1/2 -translate-x-1/2 z-30 scale-100 opacity-100 shadow-2xl';
              break;
            case 'right':
              styleClass =
                'left-1/2 translate-x-[20%] z-20 scale-90 opacity-40 blur-[1px] hover:opacity-70 hover:blur-0 cursor-pointer';
              break;
            case 'left':
              styleClass =
                'left-1/2 -translate-x-[120%] z-20 scale-90 opacity-40 blur-[1px] hover:opacity-70 hover:blur-0 cursor-pointer';
              break;
            default:
              styleClass = 'hidden';
          }

          return (
            <div
              key={slide.id}
              onClick={() => {
                if (position === 'left') prevSlide();
                if (position === 'right') nextSlide();
              }}
              className={`absolute top-1/2 -translate-y-1/2 w-[320px] md:w-[500px] h-[500px] md:h-[600px] transition-all duration-500 ease-in-out ${styleClass}`}
            >
              <DemoWindow>{slide.content}</DemoWindow>
            </div>
          );
        })}

        {/* Navigation Buttons (Floating) */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-12 p-3 rounded-full bg-white/80 backdrop-blur-sm border border-gray-100 shadow-lg text-gray-500 hover:text-black hover:scale-110 transition-all z-40 focus:outline-none"
          aria-label="Previous Demo"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-12 p-3 rounded-full bg-white/80 backdrop-blur-sm border border-gray-100 shadow-lg text-gray-500 hover:text-black hover:scale-110 transition-all z-40 focus:outline-none"
          aria-label="Next Demo"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      {/* Pagination Dots & Labels */}
      <div className="flex justify-center items-center gap-3 mt-8">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => setActiveIndex(index)}
            className={`group flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
              index === activeIndex
                ? 'bg-black text-white shadow-md'
                : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-100'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full transition-colors ${index === activeIndex ? 'bg-[#81D8D0]' : 'bg-gray-300 group-hover:bg-gray-400'}`}
            ></span>
            {slide.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PlatformDemo;
